'use client';
import { Item } from '@snack-uikit/breadcrumbs';
import { ItemProps } from '@snack-uikit/list';
import * as cheerio from 'cheerio';
import { Element } from 'domhandler';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { usePathname } from 'next/navigation';
import { Dispatch, FC, SetStateAction, useState } from 'react';

import {
  abbrInjector,
  accordionInjector,
  aToNextLinkInjector,
  captionInjector,
  commandInjector,
  ddInjector,
  dlHttpInjector,
  dlListInjector,
  dtToSnackTypographyInjector,
  guidLabelToSnackTypographyInjector,
  highlightInjector,
  hintToSnackAlertInjector,
  hToSnackTypographyInjector,
  imgToNextImageInjector,
  kbdToSnackTypographyInjector,
  liInjector,
  mathInjector,
  olArabicInjector,
  pToSnackTypographyInjector,
  sectionInjector,
  serviceCardInjector,
  serviceCardsGridInjector,
  tableInjector,
  tabsInjector,
  tagCodeInjector,
  ulInjector,
} from '@/components/dynamic';
import { APP_BASE_PATH } from '@/constant/common';
import { FEEDBACK_FORM_FALLBACK_VALUES } from '@/constant/forms';
import { BACKEND_ENDPOINTS } from '@/constant/network';
import { usePageDataCtx } from '@/hooks/ctx';
import { useCurrentPlatform } from '@/hooks/useCurrentPlatform';
import { createFeedbackHandler } from '@/root/src/services/feedback';
import { ArticleFeedbackBase, FeedbackFormValues, IsLiked } from '@/types/feedback';
import { AbsolutePath, NavListItemProps } from '@/types/navigation';
import { PageDataBreadCrumbItem } from '@/types/ssr';
import { equals, identity, isEmpty, mergeRight } from '@/utils/common';
import { getMetaTagValue } from '@/utils/metaTags';
import { resolvePath } from '@/utils/navigation';
import { isClientSide } from '@/utils/next';
import { getFullCurrentPath } from '@/utils/routes';

type MakeBreadCrumbsListPayload = {
  items: PageDataBreadCrumbItem[];
  title: string;
  push: AppRouterInstance['push'];
  pathname: AbsolutePath;
  mainPageLink: string;
};

type TableOfContentItemWrapper = FC<NavListItemProps>;

const makeItem = (link: string, label: string, push: AppRouterInstance['push']): Item => ({
  onClick: () => push(link),
  label,
  id: label.concat(link),
});

export const makeBreadCrumbsList = ({
  items,
  push,
  title,
  pathname,
  mainPageLink,
}: MakeBreadCrumbsListPayload): Item[] => [
  makeItem(mainPageLink, 'Главная', push),
  ...items.map(item => makeItem(resolvePath(pathname, item.link), item.title, push)),
  { label: title, id: title },
];

export const reactInjectors = [
  hToSnackTypographyInjector,
  tabsInjector,
  aToNextLinkInjector,
  olArabicInjector,
  ulInjector,
  pToSnackTypographyInjector,
  guidLabelToSnackTypographyInjector,
  hintToSnackAlertInjector,
  dtToSnackTypographyInjector,
  ddInjector,
  dlHttpInjector,
  sectionInjector,
  highlightInjector,
  tagCodeInjector,
  kbdToSnackTypographyInjector,
  tableInjector,
  accordionInjector,
  imgToNextImageInjector,
  captionInjector,
  mathInjector,
  dlListInjector,
  liInjector,
  serviceCardInjector,
  serviceCardsGridInjector,
  abbrInjector,
  commandInjector,
];

const getFirstH1Content = (html: string): string => {
  const $ = cheerio.load(html);
  return $('h1').first().text().trim() || '';
};

export const useSendFeedback = (
  setIsOpenThanksBlock: Dispatch<SetStateAction<boolean>>,
  setIsOpenFeedbackForm: Dispatch<SetStateAction<boolean>>,
) => {
  const [isLoading, setIsLoading] = useState(false);
  const platform = useCurrentPlatform();
  const pathname = usePathname();
  const { metaTagsStr, bodyString } = usePageDataCtx();

  const baseBody: ArticleFeedbackBase = {
    product: getMetaTagValue('product', metaTagsStr),
    document: getMetaTagValue('document', metaTagsStr),
    retainURL: isClientSide() ? getFullCurrentPath(window.location, `${APP_BASE_PATH}${pathname}`) : '',
    userAgent: isClientSide() ? window.navigator.userAgent : '',
    title: getFirstH1Content(bodyString),
    platform,
    originalUTM: '',
  };

  const fnPayload = {
    onComplete: (payload: IsLiked) => {
      if (
        payload.isLiked ||
        (Boolean(payload.comment) && !equals(FEEDBACK_FORM_FALLBACK_VALUES.comment, payload.comment))
      ) {
        setIsOpenFeedbackForm(false);
      }
      setIsLoading(false);
    },
    onSuccessRequest: () => {
      setIsOpenThanksBlock(true);
    },
    onStartRequest: () => setIsLoading(true),
    endpoint: BACKEND_ENDPOINTS.articleFeedback,
    baseBody,
  };

  return {
    isLoading,
    sendFeedback: createFeedbackHandler<IsLiked>(fnPayload),
    sendFeedbackWithComment: createFeedbackHandler<FeedbackFormValues & IsLiked>(fnPayload),
  };
};

export const removeLinkUnicode = (html: string) => {
  const cleaned = html.replace(/\uF0C1/g, '');

  return cleaned.replace(/<a\b([^>]*)>\s*<\/a>/gi, '');
};

export const createTableOfContentItem = (
  title: string,
  link: string,
  hasNested: boolean,
  Wrapper: TableOfContentItemWrapper,
): ItemProps => {
  const addTypeCollapse = mergeRight({ type: 'collapse' });
  const applyFn = hasNested ? addTypeCollapse : identity<ItemProps>;
  return applyFn({
    content: { option: title, truncate: { option: 5 } },
    id: link,
    itemWrapRender: item => (
      <Wrapper itemType='TOC' itemLink={link} hasNested={hasNested}>
        {item}
      </Wrapper>
    ),
  });
};

const processNode = ($: cheerio.CheerioAPI, Wrapper: TableOfContentItemWrapper, node?: Element): ItemProps[] => {
  return $(node)
    .children('li')
    .map((_, li) => {
      const $li = $(li);
      const $link = $li.find('> a');
      const $children = $li.children('ul').first();

      const item = createTableOfContentItem(
        $link.text().trim(),
        $link.attr('href') || '',
        !isEmpty($children),
        Wrapper,
      );

      const result: ItemProps = {
        ...item,
        ...(!isEmpty($children) && {
          type: 'collapse',
          items: processNode($, Wrapper, $children.get(0)),
        }),
      };

      return result;
    })
    .get();
};

export const createTableOfContentItems = (htmlString: string, Wrapper: TableOfContentItemWrapper): ItemProps[] => {
  const $ = cheerio.load(htmlString);

  const targetLi = $('ul > li:first-child');
  const list = targetLi.children('ul');

  const root = list.first();
  return processNode($, Wrapper, root.get(0));
};

export const removeTocDepthBlock = (html: string): string => {
  const $ = cheerio.load(html, { xml: false }, false);

  $('dl.field-list.simple')
    .filter((_, el) => {
      const content = $(el).find('dt.field-odd').first().text().trim();
      return equals(content, 'tocdepth');
    })
    .remove();

  return $.html();
};
